import { useState, useEffect, FormEvent, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
	PencilIcon,
	ExclamationTriangleIcon,
	DocumentTextIcon,
	CheckIcon,
	PhoneIcon,
	EnvelopeIcon,
	HomeIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import AppLayout from '../../components/layout/AppLayout';
import { Alert, AppLoader, Button, Modal, Spinner } from '../../components/ui';
import { staffProfileApi, ApiError } from '../../api';
import useStaffProfileStore from '../../stores/staffProfileStore';
import { AGREEMENT_SECTIONS } from './agreementContent';

// ─── Helpers ────────────────────────────────────────────────────

const formatDate = (dateStr: string | null): string => {
	if (!dateStr) return '-';
	try {
		const date = new Date(dateStr);
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	} catch {
		return dateStr;
	}
};

// US States for address dropdown
const US_STATES = [
	{ value: 'AL', label: 'Alabama' },
	{ value: 'AK', label: 'Alaska' },
	{ value: 'AZ', label: 'Arizona' },
	{ value: 'AR', label: 'Arkansas' },
	{ value: 'CA', label: 'California' },
	{ value: 'CO', label: 'Colorado' },
	{ value: 'CT', label: 'Connecticut' },
	{ value: 'DE', label: 'Delaware' },
	{ value: 'FL', label: 'Florida' },
	{ value: 'GA', label: 'Georgia' },
	{ value: 'HI', label: 'Hawaii' },
	{ value: 'ID', label: 'Idaho' },
	{ value: 'IL', label: 'Illinois' },
	{ value: 'IN', label: 'Indiana' },
	{ value: 'IA', label: 'Iowa' },
	{ value: 'KS', label: 'Kansas' },
	{ value: 'KY', label: 'Kentucky' },
	{ value: 'LA', label: 'Louisiana' },
	{ value: 'ME', label: 'Maine' },
	{ value: 'MD', label: 'Maryland' },
	{ value: 'MA', label: 'Massachusetts' },
	{ value: 'MI', label: 'Michigan' },
	{ value: 'MN', label: 'Minnesota' },
	{ value: 'MS', label: 'Mississippi' },
	{ value: 'MO', label: 'Missouri' },
	{ value: 'MT', label: 'Montana' },
	{ value: 'NE', label: 'Nebraska' },
	{ value: 'NV', label: 'Nevada' },
	{ value: 'NH', label: 'New Hampshire' },
	{ value: 'NJ', label: 'New Jersey' },
	{ value: 'NM', label: 'New Mexico' },
	{ value: 'NY', label: 'New York' },
	{ value: 'NC', label: 'North Carolina' },
	{ value: 'ND', label: 'North Dakota' },
	{ value: 'OH', label: 'Ohio' },
	{ value: 'OK', label: 'Oklahoma' },
	{ value: 'OR', label: 'Oregon' },
	{ value: 'PA', label: 'Pennsylvania' },
	{ value: 'RI', label: 'Rhode Island' },
	{ value: 'SC', label: 'South Carolina' },
	{ value: 'SD', label: 'South Dakota' },
	{ value: 'TN', label: 'Tennessee' },
	{ value: 'TX', label: 'Texas' },
	{ value: 'UT', label: 'Utah' },
	{ value: 'VT', label: 'Vermont' },
	{ value: 'VA', label: 'Virginia' },
	{ value: 'WA', label: 'Washington' },
	{ value: 'WV', label: 'West Virginia' },
	{ value: 'WI', label: 'Wisconsin' },
	{ value: 'WY', label: 'Wyoming' },
	{ value: 'DC', label: 'District of Columbia' },
];

const getStateName = (abbr: string | null): string => {
	if (!abbr) return '-';
	const found = US_STATES.find(s => s.value === abbr);
	return found ? found.label : abbr;
};

// ─── InfoCard Component ─────────────────────────────────────────

interface InfoCardProps {
	title: string;
	icon: ReactNode;
	onEdit?: () => void;
	children: ReactNode;
}

const InfoCard = ({ title, icon, onEdit, children }: InfoCardProps) => (
	<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm mb-5 overflow-hidden">
		<div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
			<h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
				{icon}
				{title}
			</h3>
			{onEdit && (
				<button
					onClick={onEdit}
					className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-all cursor-pointer"
					style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
				>
					<PencilIcon className="w-3.5 h-3.5" />
					Edit
				</button>
			)}
		</div>
		<div className="px-5 py-5">{children}</div>
	</div>
);

// ─── InfoItem Component ─────────────────────────────────────────

interface InfoItemProps {
	label: string;
	value: ReactNode;
}

const InfoItem = ({ label, value }: InfoItemProps) => (
	<div>
		<span className="block text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
			{label}
		</span>
		<span className="text-sm font-medium text-gray-900 dark:text-white">{value || '-'}</span>
	</div>
);

// ─── AgreementCard Component ────────────────────────────────────

interface AgreementCardProps {
	title: string;
	description: string;
	accepted: boolean;
	acceptedAt: string | null;
	iconGradient: string;
	onClick: () => void;
}

const AgreementCard = ({
	title,
	description,
	accepted,
	acceptedAt,
	iconGradient,
	onClick,
}: AgreementCardProps) => (
	<div
		className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden cursor-pointer transition-all hover:-translate-y-[3px] hover:shadow-lg"
		onClick={onClick}
	>
		<div className="p-5 flex items-start gap-4">
			<div
				className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
				style={{ background: iconGradient }}
			>
				<DocumentTextIcon className="w-7 h-7 text-white" />
			</div>
			<div className="flex-1 min-w-0">
				<h4 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
					{title}
				</h4>
				<p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
					{description}
				</p>
			</div>
			<span
				className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold ${
					accepted
						? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
						: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
				}`}
			>
				{accepted ? 'Accepted' : 'Pending'}
			</span>
		</div>
		<div className="px-5 py-3.5 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
			<span className="text-xs text-gray-500 dark:text-gray-400">
				{accepted ? `Accepted on ${formatDate(acceptedAt)}` : 'Not yet accepted'}
			</span>
			<button
				onClick={e => {
					e.stopPropagation();
					onClick();
				}}
				className="px-4 py-1.5 rounded-lg text-sm font-medium border-2 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-[#667eea] hover:text-[#667eea] transition-all bg-white dark:bg-gray-800 cursor-pointer"
			>
				View
			</button>
		</div>
	</div>
);

// ─── AgreementModal Component ───────────────────────────────────

interface AgreementModalProps {
	isOpen: boolean;
	onClose: () => void;
	type: '1099' | 'policy' | null;
	accepted: boolean;
	acceptedAt: string | null;
	onSigned: () => void;
}

const AgreementModal = ({
	isOpen,
	onClose,
	type,
	accepted,
	acceptedAt,
	onSigned,
}: AgreementModalProps) => {
	const [isSigning, setIsSigning] = useState(false);

	const modalTitle = type === '1099' ? '1099 Contractor Agreement' : 'Staff Policy Agreement';
	const modalSubtitle =
		type === '1099'
			? 'Tax classification and independent contractor acknowledgment'
			: 'Workplace conduct, professional standards, and operational guidelines';
	const iconGradient =
		type === '1099'
			? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
			: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

	const handleSign = async () => {
		if (isSigning || !type) return;
		setIsSigning(true);
		try {
			await staffProfileApi.acceptAgreement(type);
			toast.success(`${modalTitle} accepted successfully!`);
			onSigned();
			onClose();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to accept agreement');
		} finally {
			setIsSigning(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="xl" showHeader={false}>
			{/* Custom Header */}
			<div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
				<div className="flex items-center gap-4">
					<div
						className="w-12 h-12 rounded-xl flex items-center justify-center"
						style={{ background: iconGradient }}
					>
						<DocumentTextIcon className="w-6 h-6 text-white" />
					</div>
					<div>
						<h2 className="text-lg font-bold text-gray-900 dark:text-white">
							{modalTitle}
						</h2>
						<p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
							{modalSubtitle}
						</p>
					</div>
				</div>
			</div>

			{/* Agreement Content */}
			<div className="px-6 py-5">
				<div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 max-h-[400px] overflow-y-auto mb-5">
					<h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
						<DocumentTextIcon className="w-5 h-5 text-indigo-500" />
						Agreement Summary
					</h3>
					<p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
						This agreement sets expectations for{' '}
						<strong className="text-gray-700 dark:text-gray-300">
							professionalism
						</strong>
						, <strong className="text-gray-700 dark:text-gray-300">quality</strong>,{' '}
						<strong className="text-gray-700 dark:text-gray-300">accountability</strong>
						, and{' '}
						<strong className="text-gray-700 dark:text-gray-300">
							1099 tax status
						</strong>
						.
					</p>

					{AGREEMENT_SECTIONS.map(section => (
						<div key={section.number} className="mb-4">
							<div className="flex items-center gap-2 mb-2">
								<span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
									{section.number}. {section.title}
								</span>
								<span
									className={`px-2 py-0.5 rounded-full text-[0.65rem] font-semibold ${
										section.badgeClass === 'staff-agreement__badge--purple'
											? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
											: section.badgeClass === 'staff-agreement__badge--green'
												? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
												: section.badgeClass ===
													  'staff-agreement__badge--orange'
													? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300'
													: section.badgeClass ===
														  'staff-agreement__badge--red'
														? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
														: section.badgeClass ===
															  'staff-agreement__badge--violet'
															? 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300'
															: section.badgeClass ===
																  'staff-agreement__badge--cyan'
																? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300'
																: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
									}`}
								>
									{section.badge}
								</span>
							</div>
							<div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:text-sm [&_strong]:text-gray-700 dark:[&_strong]:text-gray-300">
								{section.content}
							</div>
						</div>
					))}
				</div>

				{/* Accepted Status */}
				{accepted && (
					<div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800">
						<CheckCircleIcon className="w-6 h-6 text-emerald-500 flex-shrink-0" />
						<div>
							<p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
								Agreement Accepted
							</p>
							<p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
								Accepted on {formatDate(acceptedAt)}
							</p>
						</div>
					</div>
				)}
			</div>

			{/* Footer */}
			<div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
				<Button variant="secondary" onClick={onClose}>
					{accepted ? 'Close' : 'Cancel'}
				</Button>
				{!accepted && (
					<Button variant="primary" isLoading={isSigning} onClick={handleSign}>
						<CheckIcon className="w-4 h-4 mr-1.5" />
						Sign Agreement
					</Button>
				)}
			</div>
		</Modal>
	);
};

// ─── AddressModal Component ─────────────────────────────────────

interface AddressModalProps {
	isOpen: boolean;
	onClose: () => void;
	initialData: {
		streetAddress: string;
		city: string;
		state: string;
		zipCode: string;
	};
	onSaved: () => void;
}

const AddressModal = ({ isOpen, onClose, initialData, onSaved }: AddressModalProps) => {
	const [streetAddress, setStreetAddress] = useState(initialData.streetAddress);
	const [city, setCity] = useState(initialData.city);
	const [state, setState] = useState(initialData.state);
	const [zipCode, setZipCode] = useState(initialData.zipCode);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setStreetAddress(initialData.streetAddress);
			setCity(initialData.city);
			setState(initialData.state);
			setZipCode(initialData.zipCode);
		}
	}, [isOpen, initialData]);

	const isValid = streetAddress.trim() && city.trim() && state.trim() && zipCode.trim();

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		if (!isValid || isSaving) return;

		setIsSaving(true);
		try {
			await staffProfileApi.updateAddress({
				streetAddress: streetAddress.trim(),
				city: city.trim(),
				state: state.trim(),
				zipCode: zipCode.trim(),
			});
			toast.success('Address updated successfully!');
			onSaved();
			onClose();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to update address');
		} finally {
			setIsSaving(false);
		}
	};

	const inputClasses =
		'w-full px-3.5 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors';

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Edit Address" size="md">
			<form onSubmit={handleSubmit}>
				<div className="mb-4">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
						Street Address <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						value={streetAddress}
						onChange={e => setStreetAddress(e.target.value)}
						className={inputClasses}
						placeholder="123 Main St, Apt 4B"
					/>
				</div>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							City <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							value={city}
							onChange={e => setCity(e.target.value)}
							className={inputClasses}
							placeholder="Los Angeles"
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
							State <span className="text-red-500">*</span>
						</label>
						<select
							value={state}
							onChange={e => setState(e.target.value)}
							className={inputClasses}
						>
							<option value="">Select State</option>
							{US_STATES.map(s => (
								<option key={s.value} value={s.value}>
									{s.label}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="mb-6">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
						ZIP Code <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						value={zipCode}
						onChange={e => setZipCode(e.target.value)}
						className={inputClasses}
						placeholder="90001"
						maxLength={10}
					/>
				</div>
				<div className="flex justify-end gap-3">
					<Button type="button" variant="secondary" onClick={onClose}>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="primary"
						disabled={!isValid}
						isLoading={isSaving}
					>
						Save Changes
					</Button>
				</div>
			</form>
		</Modal>
	);
};

// ─── EmergencyContactModal Component ────────────────────────────

interface EmergencyContactModalProps {
	isOpen: boolean;
	onClose: () => void;
	initialData: {
		name: string;
		phone: string;
		email: string;
	};
	onSaved: () => void;
}

const EmergencyContactModal = ({
	isOpen,
	onClose,
	initialData,
	onSaved,
}: EmergencyContactModalProps) => {
	const [name, setName] = useState(initialData.name);
	const [phone, setPhone] = useState(initialData.phone);
	const [email, setEmail] = useState(initialData.email);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setName(initialData.name);
			setPhone(initialData.phone);
			setEmail(initialData.email);
		}
	}, [isOpen, initialData]);

	const isValid = name.trim() && phone.trim();

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		if (!isValid || isSaving) return;

		setIsSaving(true);
		try {
			await staffProfileApi.updateEmergencyContact({
				name: name.trim(),
				phone: phone.trim(),
				email: email.trim(),
			});
			toast.success('Emergency contact updated successfully!');
			onSaved();
			onClose();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to update emergency contact');
		} finally {
			setIsSaving(false);
		}
	};

	const inputClasses =
		'w-full px-3.5 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] transition-colors';

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Edit Emergency Contact" size="md">
			<form onSubmit={handleSubmit}>
				<div className="mb-4">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
						Full Name <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						value={name}
						onChange={e => setName(e.target.value)}
						className={inputClasses}
						placeholder="Enter full name"
					/>
				</div>
				<div className="mb-4">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
						Phone <span className="text-red-500">*</span>
					</label>
					<input
						type="tel"
						value={phone}
						onChange={e => setPhone(e.target.value)}
						className={inputClasses}
						placeholder="(555) 000-0000"
					/>
				</div>
				<div className="mb-6">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
						Email
					</label>
					<input
						type="email"
						value={email}
						onChange={e => setEmail(e.target.value)}
						className={inputClasses}
						placeholder="email@example.com"
					/>
				</div>
				<div className="flex justify-end gap-3">
					<Button type="button" variant="secondary" onClick={onClose}>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="primary"
						disabled={!isValid}
						isLoading={isSaving}
					>
						Save Changes
					</Button>
				</div>
			</form>
		</Modal>
	);
};

// ─── Main Agreements Page ───────────────────────────────────────

const StaffAgreements = () => {
	const { profile, isLoadingProfile, profileError, fetchProfile } = useStaffProfileStore();
	const [searchParams, setSearchParams] = useSearchParams();

	const [showAgreementModal, setShowAgreementModal] = useState(false);
	const [agreementType, setAgreementType] = useState<'1099' | 'policy' | null>(null);
	const [showAddressModal, setShowAddressModal] = useState(false);
	const [showEmergencyContactModal, setShowEmergencyContactModal] = useState(false);

	// Fetch profile if not loaded
	useEffect(() => {
		if (!profile && !isLoadingProfile && !profileError) {
			fetchProfile();
		}
	}, [profile, isLoadingProfile, profileError, fetchProfile]);

	// Auto-open agreement modal from URL query param (e.g., ?open=policy or ?open=1099)
	useEffect(() => {
		const openParam = searchParams.get('open');
		if (openParam === 'policy' || openParam === '1099') {
			setAgreementType(openParam);
			setShowAgreementModal(true);
			setSearchParams({}, { replace: true });
		}
	}, [searchParams, setSearchParams]);

	// Loading state
	if (isLoadingProfile && !profile) {
		return <AppLoader message="Loading agreements" variant="fullscreen" />;
	}

	const openAgreementModal = (type: '1099' | 'policy') => {
		setAgreementType(type);
		setShowAgreementModal(true);
	};

	const hasPendingAgreements =
		profile && (!profile.agreement1099Accepted || !profile.policyAgreementAccepted);

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900 dark:text-white">Agreements</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					Review and manage your employment agreements and policies
				</p>
			</div>

			{/* Error Alert */}
			{profileError && (
				<Alert
					type="warning"
					icon={<ExclamationTriangleIcon className="w-8 h-8" />}
					title="Unable to Load Data"
					message={profileError}
					actions={[{ label: 'Retry', onClick: fetchProfile, primary: true }]}
					onDismiss={() => {}}
				/>
			)}

			{profile && (
				<>
					{/* Notice Banner */}
					{hasPendingAgreements && (
						<div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 border-l-4 border-amber-400 rounded-xl p-4 mb-6 flex items-center gap-4">
							<ExclamationTriangleIcon className="w-6 h-6 text-amber-500 flex-shrink-0" />
							<div>
								<p className="font-semibold text-amber-800 dark:text-amber-300 text-sm">
									Action Required
								</p>
								<p className="text-sm text-amber-700 dark:text-amber-400">
									You have pending agreements that need to be accepted. Please
									review and accept them.
								</p>
							</div>
						</div>
					)}

					{/* Required Agreements */}
					<div className="mb-6">
						<h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
							<DocumentTextIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
							Required Agreements
						</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-5">
							<AgreementCard
								title="1099 Contractor Agreement"
								description="Tax classification and independent contractor acknowledgment for commission-based work."
								accepted={profile.agreement1099Accepted}
								acceptedAt={profile.agreement1099AcceptedAt}
								iconGradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
								onClick={() => openAgreementModal('1099')}
							/>
							<AgreementCard
								title="Staff Policy Agreement"
								description="Workplace conduct, professional standards, and operational guidelines."
								accepted={profile.policyAgreementAccepted}
								acceptedAt={profile.policyAgreementAcceptedAt}
								iconGradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
								onClick={() => openAgreementModal('policy')}
							/>
						</div>
					</div>

					{/* Address */}
					<InfoCard
						title="Address"
						icon={<HomeIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
						onEdit={() => setShowAddressModal(true)}
					>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
							<InfoItem label="Street Address" value={profile.streetAddress} />
							<InfoItem label="City" value={profile.city} />
							<InfoItem label="State" value={getStateName(profile.state)} />
							<InfoItem label="ZIP Code" value={profile.zipCode} />
						</div>
					</InfoCard>

					{/* Emergency Contact */}
					<InfoCard
						title="Emergency Contact"
						icon={<PhoneIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
						onEdit={() => setShowEmergencyContactModal(true)}
					>
						{profile.emergencyContactName ? (
							<div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border-l-4 border-[#667eea]">
								<p className="font-semibold text-gray-900 dark:text-white mb-3">
									{profile.emergencyContactName}
								</p>
								<div className="flex flex-wrap gap-5">
									{profile.emergencyContactPhone && (
										<div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
											<PhoneIcon className="w-4 h-4" />
											<span>{profile.emergencyContactPhone}</span>
										</div>
									)}
									{profile.emergencyContactEmail && (
										<div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
											<EnvelopeIcon className="w-4 h-4" />
											<span>{profile.emergencyContactEmail}</span>
										</div>
									)}
								</div>
							</div>
						) : (
							<p className="text-sm text-gray-400 dark:text-gray-500">
								No emergency contact added yet. Click Edit to add one.
							</p>
						)}
					</InfoCard>

					{/* Loading spinner when refreshing */}
					{isLoadingProfile && (
						<div className="fixed inset-0 bg-black/10 flex items-center justify-center z-40 pointer-events-none">
							<Spinner size="lg" className="text-[#667eea]" />
						</div>
					)}

					{/* Modals */}
					<AgreementModal
						isOpen={showAgreementModal}
						onClose={() => setShowAgreementModal(false)}
						type={agreementType}
						accepted={
							agreementType === '1099'
								? profile.agreement1099Accepted
								: agreementType === 'policy'
									? profile.policyAgreementAccepted
									: false
						}
						acceptedAt={
							agreementType === '1099'
								? profile.agreement1099AcceptedAt
								: agreementType === 'policy'
									? profile.policyAgreementAcceptedAt
									: null
						}
						onSigned={fetchProfile}
					/>
					<AddressModal
						isOpen={showAddressModal}
						onClose={() => setShowAddressModal(false)}
						initialData={{
							streetAddress: profile.streetAddress || '',
							city: profile.city || '',
							state: profile.state || '',
							zipCode: profile.zipCode || '',
						}}
						onSaved={fetchProfile}
					/>
					<EmergencyContactModal
						isOpen={showEmergencyContactModal}
						onClose={() => setShowEmergencyContactModal(false)}
						initialData={{
							name: profile.emergencyContactName || '',
							phone: profile.emergencyContactPhone || '',
							email: profile.emergencyContactEmail || '',
						}}
						onSaved={fetchProfile}
					/>
				</>
			)}
		</AppLayout>
	);
};

export default StaffAgreements;
